import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Badge, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, LinearProgress, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  ACCEPTED_IMAGE_TYPES, deleteMedia, mediaUrl, subscribeToMedia, subscribeToMediaByIds,
  uploadMedia, type MediaDoc,
} from '../services/media';
import { setProductImages } from '../services/catalog';

/**
 * Attaches images to a product: upload new ones, pick existing ones, order them.
 *
 * ── The invariant ──────────────────────────────────────────────────────────
 * An id is written to product.imageIds ONLY after its media doc has been
 * observed READY. uploadMedia resolves as soon as the upload *starts* — the doc
 * sits at PROCESSING until the onMediaUpload trigger builds its variants — so
 * attaching on upload would leave the product pointing at an image that cannot
 * render, and permanently so if processing failed. Pending uploads therefore
 * live in their own state and are promoted in, never written straight through.
 *
 * Because of that invariant, a "missing" image here means exactly one thing:
 * someone deleted the media doc. That is why the warning can offer to remove
 * them rather than hedging.
 *
 * Attached images resolve through subscribeToMediaByIds, not the library
 * listing: the listing is a newest-200 window and the library is past 2,600
 * docs, which used to make every product in the catalogue report its photos as
 * missing.
 *
 * Order matters: the first image is used on category tiles, in search results
 * and as the Open Graph preview, so it carries an explicit Cover badge.
 */

interface PendingUpload {
  key: string;
  file: File;
  previewUrl: string;
  mediaId?: string;
  progress: number;
  phase: 'UPLOADING' | 'PROCESSING' | 'FAILED';
  error?: string;
  startedAt: number;
}

/** After this long still PROCESSING we stop implying it is imminent. */
const SLOW_PROCESSING_MS = 60_000;

export function ProductImageManager({
  productId,
  productName,
  initialImageIds,
  onSaved,
  onDirtyChange,
}: {
  productId: string;
  productName: string;
  initialImageIds: string[];
  onSaved: () => void;
  /** Lets a dialog wrapper warn before discarding unsaved work. */
  onDirtyChange?: (state: { dirty: boolean; pending: number }) => void;
}) {
  const [imageIds, setImageIds] = useState<string[]>(initialImageIds);
  const [attachedById, setAttachedById] = useState<Map<string, MediaDoc>>(new Map());
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const fileInput = useRef<HTMLInputElement>(null);

  const dirty = useMemo(
    () => imageIds.join('|') !== initialImageIds.join('|'),
    [imageIds, initialImageIds],
  );
  useEffect(() => onDirtyChange?.({ dirty, pending: pending.length }), [dirty, pending.length, onDirtyChange]);

  // Watch attached ids AND anything still processing, on one subscription — the
  // READY flip that promotes a pending upload arrives on the same listener.
  const watchIds = useMemo(
    () => [...imageIds, ...pending.map((p) => p.mediaId).filter(Boolean) as string[]],
    [imageIds, pending],
  );
  useEffect(() => subscribeToMediaByIds(watchIds, setAttachedById), [watchIds.join('|')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ticks only while something is pending, purely to age the watchdog copy.
  useEffect(() => {
    if (!pending.length) return;
    const t = setInterval(() => setNow(Date.now()), 5000);
    return () => clearInterval(t);
  }, [pending.length]);

  // Promote uploads whose doc has gone READY; surface the ones that failed.
  useEffect(() => {
    setPending((current) => {
      if (!current.length) return current;
      const keep: PendingUpload[] = [];
      const promote: string[] = [];
      for (const p of current) {
        const media = p.mediaId ? attachedById.get(p.mediaId) : undefined;
        if (media?.status === 'READY') {
          URL.revokeObjectURL(p.previewUrl);
          promote.push(p.mediaId!);
        } else if (media?.status === 'FAILED') {
          keep.push({ ...p, phase: 'FAILED', error: media.error || 'Processing failed.' });
        } else {
          keep.push(p);
        }
      }
      if (promote.length) {
        setImageIds((ids) => [...ids, ...promote.filter((id) => !ids.includes(id))]);
        setSaved(false);
      }
      return keep.length === current.length && !promote.length ? current : keep;
    });
  }, [attachedById]);

  useEffect(() => () => pending.forEach((p) => URL.revokeObjectURL(p.previewUrl)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const attached = imageIds.map((id) => attachedById.get(id)).filter(Boolean) as MediaDoc[];
  const missing = imageIds.filter((id) => !attachedById.has(id));

  const startUploads = useCallback(
    async (files: File[]) => {
      setError(null);
      const entries: PendingUpload[] = files.map((file, i) => ({
        key: `${file.name}-${file.size}-${Date.now()}-${i}`,
        file,
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        phase: 'UPLOADING',
        startedAt: Date.now(),
      }));
      setPending((p) => [...p, ...entries]);

      // Sequential, matching MediaLibrary: parallel uploads of print-resolution
      // files saturate the connection and make every progress bar useless.
      for (const entry of entries) {
        try {
          const mediaId = await uploadMedia(
            entry.file,
            { alt: productName, tags: ['product'] },
            (progress) =>
              setPending((p) => p.map((x) => (x.key === entry.key ? { ...x, progress } : x))),
          );
          setPending((p) =>
            p.map((x) => (x.key === entry.key ? { ...x, mediaId, phase: 'PROCESSING' } : x)),
          );
        } catch (e) {
          setPending((p) =>
            p.map((x) =>
              x.key === entry.key
                ? { ...x, phase: 'FAILED', error: e instanceof Error ? e.message : 'Upload failed.' }
                : x,
            ),
          );
        }
      }
    },
    [productName],
  );

  const dropPending = (key: string, alsoDeleteMedia = false) => {
    setPending((p) => {
      const entry = p.find((x) => x.key === key);
      if (entry) {
        URL.revokeObjectURL(entry.previewUrl);
        if (alsoDeleteMedia && entry.mediaId) void deleteMedia(entry.mediaId).catch(() => {});
      }
      return p.filter((x) => x.key !== key);
    });
  };

  const move = (index: number, delta: number) => {
    const next = [...imageIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setImageIds(next);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await setProductImages(productId, imageIds);
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {missing.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              size="small"
              onClick={() => {
                setImageIds((ids) => ids.filter((id) => attachedById.has(id)));
                setSaved(false);
              }}
            >
              Remove {missing.length}
            </Button>
          }
        >
          {missing.length} attached image{missing.length === 1 ? ' is' : 's are'} no longer in the
          library and will render as broken tiles on the storefront. Removing them here still needs
          a Save.
        </Alert>
      )}

      <Paper
        sx={{ p: 2 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const files = [...e.dataTransfer.files].filter((f) => ACCEPTED_IMAGE_TYPES.includes(f.type));
          if (files.length) void startUploads(files);
        }}
      >
        {attached.length === 0 && pending.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No images attached. This product will render as a blank tile on the storefront.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>
            {attached.map((item, i) => (
              <Badge
                key={item.id}
                badgeContent={i === 0 ? 'Cover' : null}
                color="primary"
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              >
                <Paper variant="outlined" sx={{ overflow: 'hidden', width: '100%' }}>
                  <Box
                    component="img"
                    src={mediaUrl(item, 'thumb')}
                    alt={item.alt}
                    sx={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }}
                  />
                  <Stack direction="row" sx={{ p: 0.5 }}>
                    <Tooltip title="Move earlier">
                      <span>
                        <IconButton size="small" disabled={i === 0} onClick={() => move(i, -1)}>
                          <ArrowBackIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move later">
                      <span>
                        <IconButton size="small" disabled={i === attached.length - 1} onClick={() => move(i, 1)}>
                          <ArrowForwardIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Detach">
                      <IconButton
                        size="small"
                        sx={{ ml: 'auto' }}
                        onClick={() => {
                          setImageIds((ids) => ids.filter((id) => id !== item.id));
                          setSaved(false);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              </Badge>
            ))}

            {pending.map((p) => {
              const slow = p.phase === 'PROCESSING' && now - p.startedAt > SLOW_PROCESSING_MS;
              return (
                <Paper key={p.key} variant="outlined" sx={{ overflow: 'hidden', position: 'relative' }}>
                  <Box
                    component="img"
                    src={p.previewUrl}
                    alt=""
                    sx={{
                      width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block',
                      opacity: p.phase === 'FAILED' ? 0.35 : 0.6,
                    }}
                  />
                  <Box sx={{ p: 1 }}>
                    {p.phase === 'UPLOADING' && (
                      <>
                        <LinearProgress variant="determinate" value={p.progress} />
                        <Typography variant="caption" color="text.secondary">
                          Uploading {Math.round(p.progress)}%
                        </Typography>
                      </>
                    )}
                    {p.phase === 'PROCESSING' && (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={14} />
                        <Typography variant="caption" color="text.secondary">
                          {slow
                            ? 'Still processing — it is in the library and will attach itself'
                            : 'Processing…'}
                        </Typography>
                        {slow && (
                          <Button size="small" onClick={() => dropPending(p.key)}>Stop waiting</Button>
                        )}
                      </Stack>
                    )}
                    {p.phase === 'FAILED' && (
                      <>
                        <Typography variant="caption" color="error.main" display="block">
                          {p.error}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Button
                            size="small"
                            onClick={() => { dropPending(p.key, true); void startUploads([p.file]); }}
                          >
                            Retry
                          </Button>
                          <Button size="small" color="inherit" onClick={() => dropPending(p.key, true)}>
                            Dismiss
                          </Button>
                        </Stack>
                      </>
                    )}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}

        <input
          ref={fileInput}
          type="file"
          hidden
          multiple
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            if (files.length) void startUploads(files);
            e.target.value = '';
          }}
        />

        <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center" flexWrap="wrap">
          <Button variant="outlined" onClick={() => fileInput.current?.click()}>
            Upload images
          </Button>
          <Button onClick={() => setPicking(true)}>Attach from library</Button>
          <Button variant="contained" onClick={save} disabled={saving || pending.length > 0}>
            Save images
          </Button>
          {pending.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {pending.length} still processing — save is disabled until they finish.
            </Typography>
          )}
          {saved && pending.length === 0 && (
            <Typography color="success.main" variant="body2">Saved.</Typography>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Drop files anywhere on this panel to upload. The first image is the cover.
        </Typography>
      </Paper>

      <PickerDialog
        open={picking}
        selected={imageIds}
        onClose={() => setPicking(false)}
        onConfirm={(ids) => { setImageIds(ids); setSaved(false); setPicking(false); }}
      />
    </Stack>
  );
}

function PickerDialog({
  open, selected, onClose, onConfirm,
}: {
  open: boolean;
  selected: string[];
  onClose: () => void;
  onConfirm: (ids: string[]) => void;
}) {
  const [chosen, setChosen] = useState<string[]>(selected);
  const [search, setSearch] = useState('');
  const [library, setLibrary] = useState<MediaDoc[]>([]);

  // Only the picker needs the library listing, and only while it is open — this
  // is the newest-N window, which is fine for browsing but must never be used to
  // resolve what a product already has attached.
  useEffect(() => (open ? subscribeToMedia(setLibrary, { limit: 500 }) : undefined), [open]);
  useEffect(() => setChosen(selected), [selected, open]);

  const ready = library.filter((m) => m.status === 'READY');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ready;
    return ready.filter(
      (m) => m.alt?.toLowerCase().includes(q) || m.tags?.some((t) => t.toLowerCase().includes(q)),
    );
  }, [ready, search]);

  const toggle = (id: string) =>
    setChosen((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Attach from library</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          placeholder="Search alt text or tags"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />
        {filtered.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Nothing matches. Use <strong>Upload images</strong> to add new ones directly.
          </Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1.5 }}>
            {filtered.map((item) => {
              const index = chosen.indexOf(item.id);
              const isChosen = index >= 0;
              return (
                <Paper
                  key={item.id}
                  variant="outlined"
                  onClick={() => toggle(item.id)}
                  sx={{
                    cursor: 'pointer', overflow: 'hidden', position: 'relative',
                    outline: isChosen ? '2px solid' : 'none', outlineColor: 'primary.main',
                  }}
                >
                  <Box
                    component="img"
                    src={mediaUrl(item, 'thumb')}
                    alt={item.alt}
                    loading="lazy"
                    sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', display: 'block' }}
                  />
                  {isChosen && (
                    <Box
                      sx={{
                        position: 'absolute', top: 4, left: 4, bgcolor: 'primary.main',
                        color: 'primary.contrastText', borderRadius: '50%', width: 22, height: 22,
                        display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
                      }}
                    >
                      {index + 1}
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto', ml: 2 }}>
          {chosen.length} selected — the first is the cover image.
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onConfirm(chosen)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}
